import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';

export const Route = createFileRoute('/theme-test')({
  component: ThemeTestPage,
});

type ThemeKey = 'current' | 'toss' | 'toss-soft' | 'toss-warm';

const themes: Record<
  ThemeKey,
  { label: string; description: string; vars: Record<string, string> }
> = {
  current: {
    label: 'Current (Mono)',
    description: '현재 모노크롬 테마',
    vars: {},
  },
  toss: {
    label: 'Toss Blue',
    description: '토스 기본 — 깔끔한 블루 + 보더리스 카드',
    vars: {
      '--primary': '#3182F6',
      '--primary-foreground': '#FFFFFF',
      '--primary-light': '#5B9CF6',
      '--primary-bg': '#EBF4FF',
      '--ring': '#3182F6',
      '--background': '#F4F4F4',
      '--card': '#FFFFFF',
      '--card-foreground': '#191F28',
      '--foreground': '#191F28',
      '--muted': '#F2F4F6',
      '--muted-foreground': '#8B95A1',
      '--secondary': '#F2F4F6',
      '--secondary-foreground': '#4E5968',
      '--accent': '#F2F4F6',
      '--accent-foreground': '#3182F6',
      '--border': 'transparent',
      '--input': '#E5E8EB',
      '--radius': '1rem',
      '--destructive': '#F04452',
      '--destructive-bg': '#FFF0F0',
      '--success': '#30C85E',
      '--success-bg': '#E8FAE8',
      '--warning': '#F5A623',
      '--warning-bg': '#FFF8E8',
      '--shadow-sm': '0 2px 8px rgba(0, 0, 0, 0.04)',
      '--shadow-md': '0 4px 16px rgba(0, 0, 0, 0.06)',
      '--shadow-lg': '0 8px 32px rgba(0, 0, 0, 0.08)',
      '--signal-green': '#30C85E',
      '--signal-green-soft': '#E8FAE8',
      '--signal-green-text': '#1B873F',
      '--signal-yellow': '#F5A623',
      '--signal-yellow-soft': '#FFF8E8',
      '--signal-yellow-text': '#B47818',
      '--signal-red': '#F04452',
      '--signal-red-soft': '#FFF0F0',
      '--signal-red-text': '#C42B39',
    },
  },
  'toss-soft': {
    label: 'Toss Soft',
    description: '토스 + 더 부드러운 배경, 큰 라운딩',
    vars: {
      '--primary': '#3182F6',
      '--primary-foreground': '#FFFFFF',
      '--primary-light': '#5B9CF6',
      '--primary-bg': '#EBF4FF',
      '--ring': '#3182F6',
      '--background': '#F8F9FA',
      '--card': '#FFFFFF',
      '--card-foreground': '#191F28',
      '--foreground': '#191F28',
      '--muted': '#F2F4F6',
      '--muted-foreground': '#8B95A1',
      '--secondary': '#F2F4F6',
      '--secondary-foreground': '#4E5968',
      '--accent': '#EBF4FF',
      '--accent-foreground': '#3182F6',
      '--border': 'transparent',
      '--input': '#E5E8EB',
      '--radius': '1.25rem',
      '--destructive': '#F04452',
      '--destructive-bg': '#FFF0F0',
      '--success': '#30C85E',
      '--success-bg': '#E8FAE8',
      '--warning': '#F5A623',
      '--warning-bg': '#FFF8E8',
      '--shadow-sm': '0 1px 4px rgba(0, 0, 0, 0.03)',
      '--shadow-md': '0 4px 12px rgba(0, 0, 0, 0.05)',
      '--shadow-lg': '0 8px 24px rgba(0, 0, 0, 0.07)',
      '--signal-green': '#30C85E',
      '--signal-green-soft': '#E8FAE8',
      '--signal-green-text': '#1B873F',
      '--signal-yellow': '#F5A623',
      '--signal-yellow-soft': '#FFF8E8',
      '--signal-yellow-text': '#B47818',
      '--signal-red': '#F04452',
      '--signal-red-soft': '#FFF0F0',
      '--signal-red-text': '#C42B39',
    },
  },
  'toss-warm': {
    label: 'Toss Warm',
    description: '토스 블루 + 따뜻한 크림 배경',
    vars: {
      '--primary': '#3182F6',
      '--primary-foreground': '#FFFFFF',
      '--primary-light': '#5B9CF6',
      '--primary-bg': '#EBF4FF',
      '--ring': '#3182F6',
      '--background': '#FAF9F7',
      '--card': '#FFFFFF',
      '--card-foreground': '#191F28',
      '--foreground': '#191F28',
      '--muted': '#F5F3F0',
      '--muted-foreground': '#8B95A1',
      '--secondary': '#F5F3F0',
      '--secondary-foreground': '#4E5968',
      '--accent': '#F0F0FF',
      '--accent-foreground': '#3182F6',
      '--border': 'transparent',
      '--input': '#E8E6E3',
      '--radius': '1rem',
      '--destructive': '#F04452',
      '--destructive-bg': '#FFF0F0',
      '--success': '#30C85E',
      '--success-bg': '#E8FAE8',
      '--warning': '#F5A623',
      '--warning-bg': '#FFF8E8',
      '--shadow-sm': '0 2px 8px rgba(0, 0, 0, 0.04)',
      '--shadow-md': '0 4px 16px rgba(0, 0, 0, 0.06)',
      '--shadow-lg': '0 8px 32px rgba(0, 0, 0, 0.08)',
      '--signal-green': '#30C85E',
      '--signal-green-soft': '#E8FAE8',
      '--signal-green-text': '#1B873F',
      '--signal-yellow': '#F5A623',
      '--signal-yellow-soft': '#FFF8E8',
      '--signal-yellow-text': '#B47818',
      '--signal-red': '#F04452',
      '--signal-red-soft': '#FFF0F0',
      '--signal-red-text': '#C42B39',
    },
  },
};

function ThemeTestPage() {
  const [activeTheme, setActiveTheme] = useState<ThemeKey>('toss');
  const isTossStyle = activeTheme !== 'current';

  // Toss-style card: no border, soft shadow
  const cardClass = isTossStyle ? 'border-none shadow-md' : '';

  return (
    <div
      className="min-h-screen bg-background p-8 transition-all duration-500"
      style={themes[activeTheme].vars as React.CSSProperties}
    >
      <div className="mx-auto max-w-5xl space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Theme Preview</h1>
          <p className="text-muted-foreground">
            토스 디자인 레퍼런스 기반 테마 비교. 클릭해서 실시간으로 전환해보세요.
          </p>
        </div>

        {/* Theme Selector */}
        <div className="grid grid-cols-4 gap-3">
          {(Object.entries(themes) as [ThemeKey, (typeof themes)[ThemeKey]][]).map(
            ([key, theme]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTheme(key)}
                className={`rounded-2xl p-4 text-left transition-all duration-300 ${
                  activeTheme === key
                    ? 'bg-primary/10 shadow-md ring-2 ring-primary scale-[1.02]'
                    : 'bg-card shadow-sm hover:shadow-md hover:scale-[1.01]'
                }`}
              >
                <div className="text-sm font-bold">{theme.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{theme.description}</div>
              </button>
            ),
          )}
        </div>

        {/* Design Philosophy */}
        {isTossStyle && (
          <div className="rounded-2xl bg-primary/5 px-5 py-4 text-sm text-primary transition-all duration-300">
            <span className="font-semibold">Toss Style</span> — 보더 없이 그림자로 카드 구분 /
            넉넉한 패딩 / 큰 숫자 강조 / 부드러운 라운딩 / 깔끔한 컬러 위계
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* Buttons */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Buttons</CardTitle>
              <CardDescription>버튼 variants</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button className={isTossStyle ? 'rounded-xl font-semibold' : ''}>Primary</Button>
              <Button variant="secondary" className={isTossStyle ? 'rounded-xl font-semibold' : ''}>
                Secondary
              </Button>
              <Button variant="outline" className={isTossStyle ? 'rounded-xl font-semibold' : ''}>
                Outline
              </Button>
              <Button variant="ghost" className={isTossStyle ? 'rounded-xl font-semibold' : ''}>
                Ghost
              </Button>
              <Button
                variant="destructive"
                className={isTossStyle ? 'rounded-xl font-semibold' : ''}
              >
                Destructive
              </Button>
              <Button variant="link">Link</Button>
            </CardContent>
          </Card>

          {/* Badges */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Badges</CardTitle>
              <CardDescription>상태 뱃지</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="success">건강</Badge>
              <Badge variant="warning">주의</Badge>
              <Badge variant="destructive">위험</Badge>
              <Badge variant="info">정보</Badge>
            </CardContent>
          </Card>

          {/* Signal Colors */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Signal System</CardTitle>
              <CardDescription>신호등 — 핵심 브랜딩</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  color: 'green' as const,
                  label: '건강 고객',
                  count: '1,234명',
                  desc: '최근 30일 내 구매',
                },
                {
                  color: 'yellow' as const,
                  label: '주의 고객',
                  count: '567명',
                  desc: '구매 주기 초과',
                },
                {
                  color: 'red' as const,
                  label: '이탈 위험',
                  count: '89명',
                  desc: '90일 이상 미구매',
                },
              ].map((item) => (
                <div
                  key={item.color}
                  className={`flex items-center gap-4 rounded-2xl p-4 transition-all duration-200 ${
                    isTossStyle ? 'hover:scale-[1.01]' : ''
                  }`}
                  style={{ background: `var(--signal-${item.color}-soft)` }}
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-full"
                    style={{ background: `var(--signal-${item.color})` }}
                  >
                    <span className="text-lg text-white">
                      {item.color === 'green' ? '♥' : item.color === 'yellow' ? '!' : '↓'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div
                      className="text-sm font-semibold"
                      style={{ color: `var(--signal-${item.color}-text)` }}
                    >
                      {item.label}
                    </div>
                    <div className="text-xs text-muted-foreground">{item.desc}</div>
                  </div>
                  <div
                    className="text-xl font-bold tabular-nums"
                    style={{ color: `var(--signal-${item.color}-text)` }}
                  >
                    {item.count}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Form Elements */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Form Elements</CardTitle>
              <CardDescription>입력 필드</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                placeholder="고객명을 검색하세요..."
                className={isTossStyle ? 'rounded-xl border-none bg-muted h-11' : ''}
              />
              <Input
                placeholder="비활성 상태"
                disabled
                className={isTossStyle ? 'rounded-xl border-none bg-muted h-11' : ''}
              />
              <div className="flex gap-2">
                <Input
                  placeholder="이메일"
                  className={`flex-1 ${isTossStyle ? 'rounded-xl border-none bg-muted h-11' : ''}`}
                />
                <Button className={isTossStyle ? 'rounded-xl h-11 px-6 font-semibold' : ''}>
                  검색
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Skeleton - Current (pulse) */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Skeleton — Pulse (현재)</CardTitle>
              <CardDescription>기본 animate-pulse</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-11 w-11 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
              <Skeleton className="h-20 w-full rounded-2xl" />
              <div className="grid grid-cols-3 gap-3">
                <Skeleton className="h-16 rounded-2xl" />
                <Skeleton className="h-16 rounded-2xl" />
                <Skeleton className="h-16 rounded-2xl" />
              </div>
            </CardContent>
          </Card>

          {/* Skeleton - Shimmer (proposed) */}
          <Card className={`transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Skeleton — Shimmer (제안)</CardTitle>
              <CardDescription>빛이 흐르는 애니메이션</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full skeleton-shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded-md skeleton-shimmer" />
                  <div className="h-3 w-1/2 rounded-md skeleton-shimmer" />
                </div>
              </div>
              <div className="h-20 w-full rounded-2xl skeleton-shimmer" />
              <div className="grid grid-cols-3 gap-3">
                <div className="h-16 rounded-2xl skeleton-shimmer" />
                <div className="h-16 rounded-2xl skeleton-shimmer" />
                <div className="h-16 rounded-2xl skeleton-shimmer" />
              </div>
            </CardContent>
          </Card>

          {/* Dashboard Preview */}
          <Card className={`col-span-2 transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Dashboard Preview</CardTitle>
              <CardDescription>실제 대시보드 스타일 미리보기</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: '전체 고객', value: '12,345', change: '+2.5%', positive: true },
                  { label: '활성 고객', value: '8,901', change: '+5.1%', positive: true },
                  { label: '이탈 위험', value: '234', change: '-12.3%', positive: true },
                  { label: '평균 LTV', value: '₩85,400', change: '-1.2%', positive: false },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className={`rounded-2xl bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 ${
                      isTossStyle ? 'shadow-sm hover:shadow-md' : 'border hover:shadow-md'
                    }`}
                  >
                    <div className="text-xs font-medium text-muted-foreground">{stat.label}</div>
                    <div className="mt-2 text-2xl font-bold tracking-tight">{stat.value}</div>
                    <div
                      className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        stat.positive
                          ? 'bg-signal-green-soft text-signal-green-text'
                          : 'bg-signal-red-soft text-signal-red-text'
                      }`}
                    >
                      {stat.change}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mini table */}
              <div
                className={`mt-6 overflow-hidden rounded-2xl ${
                  isTossStyle ? 'bg-card shadow-sm' : 'border'
                }`}
              >
                <div className="flex bg-muted/50 px-5 py-3 text-xs font-semibold text-muted-foreground">
                  <span className="flex-1">고객명</span>
                  <span className="w-28 text-center">상태</span>
                  <span className="w-28 text-right">구매액</span>
                </div>
                {[
                  { name: '김민수', grade: 'green' as const, label: '건강', amount: '₩245,000' },
                  { name: '이지영', grade: 'yellow' as const, label: '주의', amount: '₩89,000' },
                  { name: '박서준', grade: 'red' as const, label: '위험', amount: '₩12,000' },
                ].map((row, i) => (
                  <div
                    key={row.name}
                    className={`flex items-center px-5 py-3.5 transition-colors hover:bg-muted/30 ${
                      i > 0 ? 'border-t border-muted' : ''
                    }`}
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                        {row.name[0]}
                      </div>
                      <span className="text-sm font-medium">{row.name}</span>
                    </div>
                    <span className="w-28 text-center">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                        style={{
                          background: `var(--signal-${row.grade}-soft)`,
                          color: `var(--signal-${row.grade}-text)`,
                        }}
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: `var(--signal-${row.grade})` }}
                        />
                        {row.label}
                      </span>
                    </span>
                    <span className="w-28 text-right text-sm font-medium tabular-nums">
                      {row.amount}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Micro Interactions */}
          <Card className={`col-span-2 transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Micro Interactions</CardTitle>
              <CardDescription>토스 스타일 인터랙션 — 부드럽고 자연스러운 피드백</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2 text-center">
                  <div className="mb-2 text-xs text-muted-foreground">Press scale</div>
                  <Button
                    className={`w-full transition-transform duration-150 active:scale-95 ${
                      isTossStyle ? 'rounded-xl font-semibold' : ''
                    }`}
                  >
                    눌러보세요
                  </Button>
                </div>
                <div className="space-y-2 text-center">
                  <div className="mb-2 text-xs text-muted-foreground">Card lift</div>
                  <div
                    className={`cursor-pointer rounded-2xl bg-card p-4 transition-all duration-200 hover:-translate-y-1 ${
                      isTossStyle ? 'shadow-sm hover:shadow-lg' : 'border hover:shadow-lg'
                    }`}
                  >
                    <div className="text-sm font-semibold">호버</div>
                    <div className="mt-1 text-xs text-muted-foreground">살짝 떠오름</div>
                  </div>
                </div>
                <div className="space-y-2 text-center">
                  <div className="mb-2 text-xs text-muted-foreground">Soft pulse</div>
                  <div className="flex justify-center">
                    <span className="relative flex h-3 w-3">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-primary" />
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">실시간 동기화 중</div>
                </div>
                <div className="space-y-2 text-center">
                  <div className="mb-2 text-xs text-muted-foreground">Count up</div>
                  <div className="text-3xl font-bold text-primary tabular-nums">1,234</div>
                  <div className="text-xs text-muted-foreground">숫자 카운트업 (구현 예정)</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sidebar Preview */}
          <Card className={`col-span-2 transition-all duration-300 ${cardClass}`}>
            <CardHeader>
              <CardTitle>Sidebar Preview</CardTitle>
              <CardDescription>네비게이션 미리보기</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                {/* Mini sidebar */}
                <div
                  className={`w-56 shrink-0 rounded-2xl p-4 ${
                    isTossStyle ? 'bg-card shadow-sm' : 'border'
                  }`}
                >
                  <div className="mb-6 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-xs font-bold text-primary-foreground">
                      S
                    </div>
                    <div>
                      <div className="text-sm font-bold">Signal</div>
                      <div className="text-[10px] text-muted-foreground">고객 건강 점수</div>
                    </div>
                  </div>
                  <nav className="space-y-1">
                    {[
                      { label: '대시보드', active: true },
                      { label: '고객 상태', active: false },
                      { label: '재구매 추이', active: false },
                      { label: '구매주기', active: false },
                      { label: '고객 가치', active: false },
                      { label: '쿠폰 발송', active: false },
                      { label: '설정', active: false },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`cursor-pointer rounded-xl px-3 py-2 text-sm transition-all duration-150 ${
                          item.active
                            ? 'bg-primary/10 font-semibold text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        {item.label}
                      </div>
                    ))}
                  </nav>
                </div>

                {/* Mini content area */}
                <div className="flex-1 space-y-4">
                  <div>
                    <div className="text-lg font-bold">대시보드</div>
                    <div className="text-sm text-muted-foreground">
                      고객 건강 상태를 한눈에 확인하세요
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: '건강', value: '68%', color: 'green' as const },
                      { label: '주의', value: '24%', color: 'yellow' as const },
                      { label: '위험', value: '8%', color: 'red' as const },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`rounded-2xl p-4 text-center transition-all duration-200 hover:scale-[1.02] ${
                          isTossStyle ? 'shadow-sm' : 'border'
                        }`}
                        style={{ background: `var(--signal-${item.color}-soft)` }}
                      >
                        <div
                          className="text-2xl font-bold"
                          style={{ color: `var(--signal-${item.color}-text)` }}
                        >
                          {item.value}
                        </div>
                        <div className="mt-1 text-xs font-medium text-muted-foreground">
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Shimmer CSS */}
      <style>{`
        .skeleton-shimmer {
          background: linear-gradient(
            90deg,
            var(--muted) 25%,
            var(--input) 50%,
            var(--muted) 75%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
